var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import GraphQLError from './GraphQLError';
import useGraphQLClient from './useGraphQLClient';
;
/**
 * Returns the result of a GraphQL query using the specified options.
 *
 * @template TResult The expected result type of the query.
 * @template TVariables The expected variables type of the query.
 * @param {string} query The GraphQL query string.
 * @param {IOptions<TResult, TVariables>} [options] The options for the query.
 * @returns {IUseQueryRet<TResult, TVariables>} The result of the query.
 */
var useQuery = function (query, options) {
    var client = useGraphQLClient(options && options.client, options && options.guest);
    var currentVariables = options === null || options === void 0 ? void 0 : options.variables;
    var lastQueryResponseRef = React.useRef();
    var queryRet = React.useMemo(function () {
        // any time the options change, reset any cached returned value
        lastQueryResponseRef.current = undefined;
        if (options === null || options === void 0 ? void 0 : options.skip)
            return null;
        var request = {
            query: query,
            variables: currentVariables,
            operationName: options === null || options === void 0 ? void 0 : options.operationName,
            extensions: options === null || options === void 0 ? void 0 : options.extensions,
        };
        return client.ExecuteQuery(request, options === null || options === void 0 ? void 0 : options.fetchPolicy);
    }, [
        client,
        options === null || options === void 0 ? void 0 : options.skip,
        query,
        JSON.stringify(currentVariables || null),
        options === null || options === void 0 ? void 0 : options.operationName,
        JSON.stringify((options === null || options === void 0 ? void 0 : options.extensions) || null),
        options === null || options === void 0 ? void 0 : options.fetchPolicy,
    ]);
    var _a = React.useState((queryRet === null || queryRet === void 0 ? void 0 : queryRet.result) || null), data = _a[0], setData = _a[1];
    React.useEffect(function () {
        // subscribe to any updates to this query by calls to refetch
        var unsubscribe = queryRet === null || queryRet === void 0 ? void 0 : queryRet.subscribe(function (newData) {
            setData(newData || null);
        });
        // ensure that the data is up to date (if identical instance, no redrawing will occur)
        setData((queryRet === null || queryRet === void 0 ? void 0 : queryRet.result) || null);
        // unsubscribe when the component is dismounted
        return unsubscribe;
    }, [queryRet]);
    // if skipped, return...nothing
    if (!queryRet) {
        return {
            loading: false,
            refetch: function () { return Promise.reject(new Error("Cannot refetch a skipped query")); },
        };
    }
    // prep refresh function to throw a GraphQLError if there were any query errors
    var refresh = function () {
        return queryRet.refresh().then(function (data) {
            if (data.data && !(data.errors && data.errors.length))
                return Promise.resolve(data);
            return Promise.reject(new GraphQLError(data));
        });
    };
    // return the query data, unless there was errors and it's reloading
    var anyErrors = !!(data && data.errors && data.errors.length);
    if (queryRet.loading && anyErrors)
        return {
            loading: true,
            refetch: refresh,
        };
    else
        return __assign(__assign({}, data), { error: anyErrors ? new GraphQLError(data) : undefined, loading: queryRet.loading, refetch: refresh });
};
export default useQuery;
